/*******************************************

* [COMPONENT]: CART DRAWER

  * Internal events:
    * - l:cart:updated: Fired when the cart is updated
    * - l:cart:discount:applied: Fired when a discount code is applied
  * External events
    * - l:product:added: Fired when a product is added to the cart
    * - l:cart:error: Fired when a cart error occurs
    *   ++ add 2 items into cart while product has only 1 item
********************************************/

if (!customElements.get('l-cart-drawer')) {
	class LTACartDrawer extends LTADrawer {
		constructor() {
			super()

			this.updatedVariants = []
			this._onQuantityChange = l_debounce(this._onQuantityChange.bind(this), 200)
			this._onRemoveLineItem = this._onRemoveLineItem.bind(this)
			this._onCartUpdate = this._onCartUpdate.bind(this)
		}

		connectedCallback() {
			if (super.connectedCallback) super.connectedCallback()

			this.updatedVariants = []

			this.addEventListener('change', this._onQuantityChange)
			this.addEventListener('click', this._onRemoveLineItem)

			document.addEventListener('l:product:added', this._onCartUpdate)
			document.addEventListener('l:cart:error', this._onCartUpdate)
			document.addEventListener('l:cart:discount:applied', this._onCartUpdate)
		}

		disconnectedCallback() {
			if (super.disconnectedCallback) super.disconnectedCallback()

			this.removeEventListener('change', this._onQuantityChange)
			this.removeEventListener('click', this._onRemoveLineItem)

			document.removeEventListener('l:product:added', this._onCartUpdate)
			document.removeEventListener('l:cart:error', this._onCartUpdate)
			document.removeEventListener('l:cart:discount:applied', this._onCartUpdate)
		}

		drawer_close() {
			if (super.drawer_close) super.drawer_close()

			this.dispatchEvent(
				new CustomEvent('l:cart:closed', {
					bubbles: true,
					detail: { updatedVariants: this.updatedVariants },
				})
			)

			this.updatedVariants = []
		}

		_onRemoveLineItem(event) {
			const removeBtn = event.target.closest('[data-remove-line-item]')
			if (!removeBtn) return

			event.preventDefault()

			const lineItemEl = event.target.closest('[data-drawer-line-item]')
			if (lineItemEl) {
				this._updateCartLine(lineItemEl, 0)
			}
		}

		_onQuantityChange(event) {
			const lineItemEl = event.target.closest('[data-drawer-line-item]')
			if (!lineItemEl) return

			const quantity = parseInt(event.target.value || 1, 10)

			this._updateCartLine(lineItemEl, quantity)
		}

		_updateCartLine(lineItemEl, quantity) {
			if (!lineItemEl) return

			this.classList.add('is-loading')

			const line = parseInt(lineItemEl.dataset.index, 10)
			const variantId = lineItemEl.dataset.variantId

			ltaCartService
				.changeCartLineItem({
					line,
					quantity,
					sections_url: window.location.pathname,
					sections: this._getSectionsToRender().map((section) => section.id),
				})
				.then((cart) => {
					if (cart?.status === 422) {
						this._handleCartError(cart, lineItemEl)

						return
					}

					this._updateCartData(cart.sections[this.dataset.sectionId])

					this.dispatchEvent(new CustomEvent('l:cart:updated', { detail: { cart }, bubbles: true }))
					this.updatedVariants.push(variantId)
				})
				.catch((error) => {
					console.error(error)
				})
				.finally(() => {
					this.classList.remove('is-loading')
				})
		}

		_handleCartError(error = { description: '' }, lineItemEl) {
			// Add error message to line item
			const errorMessageEl = lineItemEl.querySelector('[data-error-message]')

			if (errorMessageEl) {
				errorMessageEl.textContent = error.description
				errorMessageEl.parentElement.classList.remove('l-hidden')
			}
		}

		_onCartUpdate() {
			this.drawer_open?.()
			this.classList.add('is-loading')

			fetch(`${window.LTATheme.routes.root_url}?section_id=${this.dataset.sectionId}`)
				.then((res) => res.text())
				.then((html) => {
					this._updateCartData(html)
				})
				.finally(() => {
					this.classList.remove('is-loading')
				})
		}

		_updateCartData(html) {
			if (!this.classList.contains('is-active')) this.drawer_open?.()

			const newCartDrawerHTML = l_getSectionDOM(html, '#cart-drawer')
			this.classList.toggle('is-empty', newCartDrawerHTML?.classList.contains('is-empty'))

			const selectors = ['[data-drawer-header]', '[data-cart-inner]', '[data-cart-footer]']

			selectors.forEach((selector) => {
				const newSection = newCartDrawerHTML?.querySelector(selector)
				const oldSection = this.querySelector(selector)

				console.log(newSection)
				console.log(oldSection)

				if (newSection && oldSection) {
					oldSection.innerHTML = newSection.innerHTML
					oldSection.classList.remove('l-hidden')
				}
				if (!newSection) oldSection?.classList.add('l-hidden')
			})
		}

		_getSectionsToRender() {
			return [
				{
					id: this.dataset.sectionId,
					selector: '.shopify-section',
				},
			]
		}
	}

	customElements.define('l-cart-drawer', LTACartDrawer)
}

/*******************************************
 * [COMPONENT]: SHIPPING BAR
 ********************************************/

if (!customElements.get('l-shipping-bar')) {
	class LTAShippingBar extends HTMLElement {
		constructor() {
			super()

			this.elements = {}
		}

		connectedCallback() {
			this._cacheElements()
			this._render()
		}

		_cacheElements() {
			this.elements = {
				label: this.querySelector('[data-label]'),
				progressBar: this.querySelector('[data-progress-bar]'),
				srOnly: this.querySelector('[data-sr-only]'),
			}
		}

		_getCartData() {
			const ds = this.dataset
			return {
				cartTotal: parseFloat(ds.cartTotal),
				thresholdAmount: parseFloat(ds.thresholdAmount),
				cartCurrency: ds.cartCurrency,
				shopCurrency: ds.shopCurrency,
				progressMessage: ds.progressMessage,
				successMessage: ds.successMessage,
			}
		}

		_getProgressData({ cartTotal, thresholdAmount, shopCurrency, cartCurrency }) {
			const convertedThreshold = Currency.convert(thresholdAmount, shopCurrency, cartCurrency)
			const remaining = Math.max(0, convertedThreshold - cartTotal)
			const progress = convertedThreshold ? Math.min((cartTotal / convertedThreshold) * 100, 100) : 0
			return { progress, remaining, convertedThreshold }
		}

		_render() {
			const { label, progressBar, srOnly } = this.elements
			const cartData = this._getCartData()

			if (isNaN(cartData.cartTotal) || isNaN(cartData.thresholdAmount) || !progressBar || !label || !srOnly) {
				return
			}

			const { progress, remaining } = this._getProgressData(cartData)

			progressBar.style.setProperty('--l-progress-bar-width', `${progress}%`)
			label.innerHTML =
				progress < 100 ? cartData.progressMessage.replace('[money]', l_formatMoney(remaining)) : cartData.successMessage
			progressBar.setAttribute('aria-valuenow', progress.toFixed(0))
			srOnly.innerHTML = `${progress.toFixed(0)}%`
		}
	}

	customElements.define('l-shipping-bar', LTAShippingBar)
}

